import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Eye, Phone, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrivacySettings = ({ user }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/privacy`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (key, value) => {
    const updatedSettings = { ...settings, [key]: value };
    setSettings(updatedSettings);
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/privacy`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Einstellung gespeichert');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Fehler beim Speichern');
      // Revert on error
      loadSettings();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            <div className="h-24 bg-gray-100 rounded"></div>
            <div className="h-24 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-6 h-6 text-green-600" />
        <h2 className="text-2xl font-bold text-gray-900">Datenschutz-Einstellungen</h2>
      </div>

      <div className="space-y-6">
        {/* Provider-specific settings */}
        {user?.role === 'provider' && (
          <div className="border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <Phone className="w-5 h-5 text-gray-600 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Telefonnummer-Sichtbarkeit</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Lege fest, wann deine Telefonnummer für Kunden sichtbar ist.
                </p>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="phone_visibility"
                      checked={settings?.show_phone_immediately ?? true}
                      onChange={() => handleUpdate('show_phone_immediately', true)}
                      className="mt-1 w-4 h-4 text-[#2B2142] border-gray-300 focus:ring-[#2B2142]"
                      disabled={saving}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 group-hover:text-[#2B2142]">
                        Sofort anzeigen
                      </div>
                      <div className="text-sm text-gray-600">
                        Deine Telefonnummer ist auf deinem Profil sichtbar
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="phone_visibility"
                      checked={settings?.show_phone_immediately === false}
                      onChange={() => handleUpdate('show_phone_immediately', false)}
                      className="mt-1 w-4 h-4 text-[#2B2142] border-gray-300 focus:ring-[#2B2142]"
                      disabled={saving}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 group-hover:text-[#2B2142]">
                        Nur nach Anfrage
                      </div>
                      <div className="text-sm text-gray-600">
                        Kunden sehen zunächst nur deine E-Mail-Adresse. Telefonnummer wird nach Kontaktaufnahme geteilt.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer-specific settings */}
        {user?.role === 'customer' && (
          <div className="border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <User className="w-5 h-5 text-gray-600 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Namensanzeige bei Bewertungen</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Bestimme, wie dein Name bei Kommentaren und Bewertungen angezeigt wird.
                </p>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="name_display"
                      checked={settings?.review_name_display === 'full'}
                      onChange={() => handleUpdate('review_name_display', 'full')}
                      className="mt-1 w-4 h-4 text-[#2B2142] border-gray-300 focus:ring-[#2B2142]"
                      disabled={saving}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 group-hover:text-[#2B2142]">
                        Vollständiger Name
                      </div>
                      <div className="text-sm text-gray-600">
                        Beispiel: "Max Mustermann"
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="name_display"
                      checked={settings?.review_name_display === 'first_initial'}
                      onChange={() => handleUpdate('review_name_display', 'first_initial')}
                      className="mt-1 w-4 h-4 text-[#2B2142] border-gray-300 focus:ring-[#2B2142]"
                      disabled={saving}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 group-hover:text-[#2B2142]">
                        Vorname + Anfangsbuchstabe
                      </div>
                      <div className="text-sm text-gray-600">
                        Beispiel: "Max M." (mehr Privatsphäre)
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info box */}
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Eye className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-green-900 mb-1">Deine Daten, deine Kontrolle</h4>
              <p className="text-sm text-green-800">
                Diese Einstellungen helfen dir, deine Privatsphäre zu schützen. 
                Mehr Informationen zu unseren Datenschutzpraktiken findest du in unserer{' '}
                <a href="/datenschutz" className="underline font-semibold hover:text-green-900">
                  Datenschutzerklärung
                </a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;
